import React, { memo, useCallback, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";

interface McpChatHeaderProps {
  isWsConnected: boolean;
  hasMessages: boolean;
  isLoading: boolean;
  chatMode: import("../shared/mcp-chat-types").ChatMode;
  planMode?: boolean;
  onClearChat: () => void;
  onClose: () => void;
  onDragStart?: (e: React.MouseEvent) => void;
  isDragging?: boolean;
  /** When true, the header is rendered inside a docked panel (no close button). */
  isDocked?: boolean;
  onSearchToggle?: () => void;
  isSearchOpen?: boolean;
  /** Show model selector item (kuika.com users only). */
  showModelSelector?: boolean;
  /** Whether the model selector popover is open. */
  isModelSelectorOpen?: boolean;
  /** Toggle the model selector popover. */
  onModelSelectorToggle?: () => void;
  /** Whether any model overrides are active. */
  hasModelOverrides?: boolean;
  /** Open the revert-history modal. Hidden when the handler is omitted. */
  onHistoryOpen?: () => void;
  /** True when the history modal is currently open — lights up the button. */
  isHistoryOpen?: boolean;
  /** Toggle full-screen. Hidden (docked) when the handler is omitted. */
  onFullScreenToggle?: () => void;
  /** Whether the panel is currently full-screen. */
  isFullScreen?: boolean;
}

const McpChatHeader: React.FC<McpChatHeaderProps> = ({
  isWsConnected,
  hasMessages,
  isLoading,
  chatMode,
  planMode,
  onClearChat,
  onClose,
  onDragStart,
  isDragging,
  isDocked,
  onSearchToggle,
  isSearchOpen,
  showModelSelector,
  isModelSelectorOpen,
  onModelSelectorToggle,
  hasModelOverrides,
  onHistoryOpen,
  isHistoryOpen,
  onFullScreenToggle,
  isFullScreen
}) => {
  const [clearConfirmOpen, setClearConfirmOpen] = useState<boolean>(false);

  const handleMouseDown = (e: React.MouseEvent): void => {
    onDragStart?.(e);
  };

  const handleClearConfirm = useCallback((): void => {
    setClearConfirmOpen(false);
    onClearChat();
  }, [onClearChat]);

  const handleClearCancel = useCallback((): void => {
    setClearConfirmOpen(false);
  }, []);

  return (
    <div className={`mcp-chat__header${isDragging ? " mcp-chat__header--dragging" : ""}`}>
      <div className="mcp-chat__header-left">
        <div
          className="mcp-chat__header-drag-icon"
          onMouseDown={handleMouseDown}
          role="button"
          aria-label="Drag to move panel"
        >
          <KuikaSvg name="grip-vertical" type="common" width={18} height={18} />
        </div>
        <div className="mcp-chat__header-icon">
          <KuikaSvg name="sparkles" type="datasource-icons" width={16} height={16} />
        </div>
        <span className="mcp-chat__header-title" data-testid="mcpChatPanelTitle">
          Kuika AI
        </span>
        <span
          className={`mcp-chat__status-dot ${isWsConnected ? "mcp-chat__status-dot--connected" : "mcp-chat__status-dot--disconnected"}`}
          title={isWsConnected ? "Connected" : "Disconnected"}
          role="status"
          aria-label={isWsConnected ? "Connected" : "Disconnected"}
        />
        {/* Mode (Designer/Builder/Ask) is shown + changed in the composer's mode
            selector, not duplicated here. Plan stays — it's an orthogonal active
            state, not one of the three modes. */}
        {planMode && (
          <KuikaTooltip content="Plan mode active — AI explores and creates a plan before executing" position="bottom">
            <span className="mcp-chat__mode-badge mcp-chat__mode-badge--plan" data-testid="mcpChatPlanBadge">
              Plan
            </span>
          </KuikaTooltip>
        )}
      </div>
      <div className="mcp-chat__header-right">
        {onHistoryOpen && (
          <KuikaButton
            type="tertiary"
            color="neutral"
            isIcon
            size="sm"
            onClick={onHistoryOpen}
            aria-label="Revert history"
            className={isHistoryOpen ? "mcp-chat__header-btn--active" : undefined}
          >
            {/* clock-with-counterclockwise-arrow — reads as "history" */}
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 3v5h5" />
              <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
              <path d="M12 7v5l4 2" />
            </svg>
          </KuikaButton>
        )}
        {onSearchToggle && (
          <KuikaButton
            type="tertiary"
            color="neutral"
            isIcon
            size="sm"
            onClick={onSearchToggle}
            aria-label={isSearchOpen ? "Close search" : "Search messages"}
            className={isSearchOpen ? "mcp-chat__header-btn--active" : undefined}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </KuikaButton>
        )}
        {showModelSelector && onModelSelectorToggle && (
          <KuikaButton
            type="tertiary"
            color="neutral"
            isIcon
            size="sm"
            onClick={onModelSelectorToggle}
            aria-label="Model settings"
            className={`${isModelSelectorOpen ? "mcp-chat__header-btn--active" : ""}${hasModelOverrides ? " mcp-chat__header-btn--has-overrides" : ""}`}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3v3M12 18v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M3 12h3M18 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
            </svg>
          </KuikaButton>
        )}
        {hasMessages && (
          <KuikaPopover
            visible={clearConfirmOpen}
            onVisibleChange={setClearConfirmOpen}
            position="bottom"
            align="end"
            title="Clear this chat?"
            className="mcp-chat__clear-popover"
            content={
              <div className="mcp-chat__clear-confirm">
                <p className="mcp-chat__clear-confirm-text">
                  This removes the current conversation. This can't be undone.
                </p>
                <div className="mcp-chat__clear-confirm-actions">
                  <KuikaButton type="tertiary" color="neutral" size="sm" onClick={handleClearCancel}>
                    Cancel
                  </KuikaButton>
                  <KuikaButton
                    type="primary"
                    color="danger"
                    size="sm"
                    onClick={handleClearConfirm}
                    data-testid="clearChatConfirmButton"
                  >
                    Clear
                  </KuikaButton>
                </div>
              </div>
            }
          >
            <KuikaButton
              type="tertiary"
              color="neutral"
              isIcon
              size="sm"
              disabled={isLoading}
              aria-label="Clear chat"
              className={clearConfirmOpen ? "mcp-chat__header-btn--active" : undefined}
            >
              <KuikaSvg name="trash" type="common" width={15} height={15} />
            </KuikaButton>
          </KuikaPopover>
        )}
        {onFullScreenToggle && (
          <KuikaButton
            type="tertiary"
            color="neutral"
            isIcon
            size="sm"
            onClick={onFullScreenToggle}
            aria-label={isFullScreen ? "Restore size" : "Full screen"}
            className={isFullScreen ? "mcp-chat__header-btn--active" : undefined}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isFullScreen ? (
                <>
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </>
              ) : (
                <>
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M16 3h3a2 2 0 0 1 2 2v3" />
                  <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
                  <path d="M16 21h3a2 2 0 0 1 2-2v-3" />
                </>
              )}
            </svg>
          </KuikaButton>
        )}
        {!isDocked && (
          <KuikaButton
            type="tertiary"
            color="neutral"
            isIcon
            size="sm"
            onClick={onClose}
            aria-label="Close chat"
            data-testid="mcpChatCloseButton"
          >
            <KuikaSvg name="x" type="common" width={16} height={16} />
          </KuikaButton>
        )}
      </div>
    </div>
  );
};

export default memo(McpChatHeader);
